import { useState, useCallback, useEffect, useRef, useMemo, createContext, useContext } from "react";
import { useDrag, useDragLayer } from "react-dnd";
import { useContentEditingSafe } from "./ContentEditingContext";
import { useTemplateType, useRelativePath } from "./TemplateContext";
import {
  Type,
  AlignLeft,
  List,
  Quote,
  Info,
  Minus,
  LayoutGrid,
  Image,
  CreditCard,
  MousePointerClick,
  BarChart3,
  GripVertical,
  ChevronDown,
  MoveVertical,
  PanelLeft,
  X,
  Package,
  PanelLeftClose,
  Mail,
  Award,
  ListOrdered,
  ShieldCheck,
  Layers,
  MessageSquareQuote,
  Columns2,
  Handshake,
  Newspaper,
  ShoppingBag,
  Grid3X3,
  Megaphone,
  Truck,
  HelpCircle,
  Search,
  Bookmark,
  Trash2,
  RefreshCw,
  Link2,
  Video,
  Table2,
  LayoutList,
  Clock,
  Tag,
  ListChecks,
  Bell,
  Users,
  Timer,
  Share2,
  BarChart,
  ScrollText,
  FileText,
  Save,
  Plus,
  Loader2,
  Copy,
  Replace,
} from "lucide-react";
import { motion, AnimatePresence } from "motion/react";
import type { SavedSection } from "../../utils/savedSectionsApi";
import { listSavedSections, deleteSavedSection } from "../../utils/savedSectionsApi";
import type { PageTemplate, PageTemplateBlock } from "../../utils/pageTemplatesApi";
import { listPageTemplates, savePageTemplate, deletePageTemplate } from "../../utils/pageTemplatesApi";

/* ────── DnD Constants (shared) ────── */

export const DND_PALETTE_BLOCK = "PALETTE_BLOCK";
export const DND_ZONE_BLOCK = "ZONE_BLOCK";

/* ────── Block Type Definitions ────── */

export interface PaletteBlockType {
  type: string;
  label: string;
  description: string;
  Icon: typeof Type;
  category: "content" | "media" | "elements" | "design" | "sections";
  /** Which template type this block is available in. "all" = always shown. */
  scope: "all" | "branding" | "store";
}

export const ALL_BLOCK_TYPES: PaletteBlockType[] = [
  /* ── Content ── */
  { type: "heading", label: "\u05DB\u05D5\u05EA\u05E8\u05EA", description: "\u05DB\u05D5\u05EA\u05E8\u05EA \u05DE\u05E9\u05E0\u05D4 \u05D7\u05D3\u05E9\u05D4", Icon: Type, category: "content", scope: "all" },
  { type: "text", label: "\u05D8\u05E7\u05E1\u05D8", description: "\u05E4\u05E1\u05E7\u05EA \u05D8\u05E7\u05E1\u05D8 \u05D7\u05D5\u05E4\u05E9\u05D9", Icon: AlignLeft, category: "content", scope: "all" },
  { type: "list", label: "\u05E8\u05E9\u05D9\u05DE\u05D4", description: "\u05E8\u05E9\u05D9\u05DE\u05EA \u05E0\u05E7\u05D5\u05D3\u05D5\u05EA", Icon: List, category: "content", scope: "all" },
  /* ── Media ── */
  { type: "image", label: "\u05EA\u05DE\u05D5\u05E0\u05D4", description: "\u05EA\u05DE\u05D5\u05E0\u05D4 \u05E2\u05DD \u05DB\u05D9\u05EA\u05D5\u05D1", Icon: Image, category: "media", scope: "all" },
  { type: "gallery", label: "\u05D2\u05DC\u05E8\u05D9\u05D4", description: "\u05E8\u05E9\u05EA \u05EA\u05DE\u05D5\u05E0\u05D5\u05EA", Icon: LayoutGrid, category: "media", scope: "all" },
  { type: "videoEmbed", label: "\u05E1\u05E8\u05D8\u05D5\u05DF \u05DE\u05D5\u05D8\u05DE\u05E2", description: "\u05E7\u05D9\u05E9\u05D5\u05E8 \u05DC\u05E1\u05E8\u05D8\u05D5\u05DF YouTube/Vimeo \u05DC\u05D4\u05D8\u05DE\u05E2\u05D4 \u05D1\u05D0\u05EA\u05E8", Icon: Video, category: "media", scope: "all" },
  /* ── Elements ── */
  { type: "card", label: "\u05DB\u05E8\u05D8\u05D9\u05E1", description: "\u05DB\u05E8\u05D8\u05D9\u05E1 \u05E2\u05DD \u05D0\u05D9\u05D9\u05E7\u05D5\u05DF \u05D5\u05D8\u05E7\u05E1\u05D8", Icon: CreditCard, category: "elements", scope: "all" },
  { type: "ctaButton", label: "\u05DB\u05E4\u05EA\u05D5\u05E8 CTA", description: "\u05DB\u05E4\u05EA\u05D5\u05E8 \u05E7\u05E8\u05D9\u05D0\u05D4 \u05DC\u05E4\u05E2\u05D5\u05DC\u05D4", Icon: MousePointerClick, category: "elements", scope: "all" },
  { type: "linkButton", label: "\u05DB\u05E4\u05EA\u05D5\u05E8 \u05E7\u05D9\u05E9\u05D5\u05E8", description: "\u05DB\u05E4\u05EA\u05D5\u05E8 \u05DC\u05E0\u05D9\u05D5\u05D5\u05D8 \u05DC\u05E2\u05DE\u05D5\u05D3 \u05E4\u05E0\u05D9\u05DE\u05D9 \u05D0\u05D5 \u05D7\u05D9\u05E6\u05D5\u05E0\u05D9", Icon: Link2, category: "elements", scope: "all" },
  { type: "stats", label: "\u05E1\u05D8\u05D8\u05D9\u05E1\u05D8\u05D9\u05E7\u05D5\u05EA", description: "\u05E9\u05D5\u05E8\u05EA \u05DE\u05E1\u05E4\u05E8\u05D9\u05DD \u05DE\u05E8\u05E9\u05D9\u05DE\u05D4", Icon: BarChart3, category: "elements", scope: "all" },
  { type: "accordion", label: "\u05D0\u05E7\u05D5\u05E8\u05D3\u05D9\u05D5\u05DF", description: "\u05E9\u05D0\u05DC\u05D4 \u05D5\u05EA\u05E9\u05D5\u05D1\u05D4 \u05E0\u05E4\u05EA\u05D7\u05EA", Icon: ChevronDown, category: "elements", scope: "all" },
  { type: "table", label: "\u05D8\u05D1\u05DC\u05D4", description: "\u05D8\u05D1\u05DC\u05D4 \u05E2\u05DD \u05E9\u05D5\u05E8\u05D5\u05EA \u05D5\u05E2\u05DE\u05D5\u05D3\u05D5\u05EA \u05E0\u05D9\u05EA\u05E0\u05EA \u05DC\u05E2\u05E8\u05D9\u05DB\u05D4", Icon: Table2, category: "elements", scope: "all" },
  { type: "tabs", label: "\u05D8\u05D0\u05D1\u05D9\u05DD", description: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05D5\u05EA \u05E2\u05DD \u05EA\u05D5\u05DB\u05DF \u05DE\u05EA\u05D7\u05DC\u05E3 \u2014 \u05DC\u05D7\u05E6\u05D5 \u05DC\u05D4\u05D7\u05DC\u05E4\u05D4", Icon: LayoutList, category: "elements", scope: "all" },
  { type: "timeline", label: "\u05E6\u05D9\u05E8 \u05D6\u05DE\u05DF", description: "\u05E6\u05D9\u05E8 \u05D6\u05DE\u05DF \u05D0\u05E0\u05DB\u05D9 \u05E2\u05DD \u05E9\u05DC\u05D1\u05D9\u05DD \u05D5\u05D0\u05D9\u05E8\u05D5\u05E2\u05D9\u05DD", Icon: Clock, category: "elements", scope: "all" },
  { type: "pricingCards", label: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05EA\u05DE\u05D7\u05D5\u05E8", description: "\u05D4\u05E9\u05D5\u05D5\u05D0\u05EA \u05DE\u05E1\u05DC\u05D5\u05DC\u05D9\u05DD / \u05D7\u05D1\u05D9\u05DC\u05D5\u05EA \u05E2\u05DD \u05DE\u05D7\u05D9\u05E8 \u05D5\u05E4\u05D9\u05E6'\u05E8\u05D9\u05DD", Icon: Tag, category: "elements", scope: "all" },
  { type: "toggleList", label: "\u05E8\u05E9\u05D9\u05DE\u05EA \u05E6'\u05E7\u05DC\u05D9\u05E1\u05D8", description: "\u05E8\u05E9\u05D9\u05DE\u05EA \u05E4\u05E8\u05D9\u05D8\u05D9\u05DD \u05E2\u05DD \u05E1\u05D9\u05DE\u05D5\u05DF V \u05E0\u05D9\u05EA\u05DF \u05DC\u05E1\u05D9\u05DE\u05D5\u05DF", Icon: ListChecks, category: "elements", scope: "all" },
  { type: "alertBanner", label: "\u05D1\u05D0\u05E0\u05E8 \u05D4\u05EA\u05E8\u05D0\u05D4", description: "\u05D1\u05D0\u05E0\u05E8 \u05E6\u05D1\u05E2\u05D5\u05E0\u05D9 \u05E2\u05DD \u05D0\u05D9\u05D9\u05E7\u05D5\u05DF \u05D5\u05D8\u05E7\u05E1\u05D8 \u05D4\u05EA\u05E8\u05D0\u05D4", Icon: Bell, category: "elements", scope: "all" },
  { type: "teamCards", label: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05E6\u05D5\u05D5\u05EA", description: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05D7\u05D1\u05E8\u05D9 \u05E6\u05D5\u05D5\u05EA \u05E2\u05DD \u05EA\u05DE\u05D5\u05E0\u05D4, \u05E9\u05DD \u05D5\u05EA\u05E4\u05E7\u05D9\u05D3", Icon: Users, category: "elements", scope: "all" },
  { type: "countdown", label: "\u05E1\u05E4\u05D9\u05E8\u05D4 \u05DC\u05D0\u05D7\u05D5\u05E8", description: "\u05E1\u05E4\u05D9\u05E8\u05D4 \u05DC\u05D0\u05D7\u05D5\u05E8 \u05E2\u05DD \u05D9\u05DE\u05D9\u05DD, \u05E9\u05E2\u05D5\u05EA \u05D5\u05D3\u05E7\u05D5\u05EA", Icon: Timer, category: "elements", scope: "all" },
  { type: "socialLinks", label: "\u05E7\u05D9\u05E9\u05D5\u05E8\u05D9\u05DD \u05D7\u05D1\u05E8\u05EA\u05D9\u05D9\u05DD", description: "\u05E1\u05E8\u05D2\u05DC \u05D0\u05D9\u05D9\u05E7\u05D5\u05E0\u05D9 \u05E8\u05E9\u05EA\u05D5\u05EA \u05D7\u05D1\u05E8\u05EA\u05D9\u05D5\u05EA \u05E2\u05DD \u05E7\u05D9\u05E9\u05D5\u05E8\u05D9\u05DD", Icon: Share2, category: "elements", scope: "all" },
  { type: "progressBars", label: "\u05E1\u05E8\u05D2\u05DC\u05D9 \u05D4\u05EA\u05E7\u05D3\u05DE\u05D5\u05EA", description: "\u05E1\u05E8\u05D2\u05DC\u05D9 \u05D4\u05EA\u05E7\u05D3\u05DE\u05D5\u05EA \u05E2\u05DD \u05EA\u05D5\u05D5\u05D9\u05D5\u05EA \u05D5\u05D0\u05D7\u05D5\u05D6\u05D9\u05DD", Icon: BarChart, category: "elements", scope: "all" },
  { type: "marquee", label: "\u05D8\u05E7\u05E1\u05D8 \u05E8\u05E5", description: "\u05D8\u05E7\u05E1\u05D8 \u05E0\u05E2 \u05E8\u05E6\u05D9\u05E3 \u2014 \u05D4\u05D5\u05D3\u05E2\u05D5\u05EA, \u05DE\u05D1\u05E6\u05E2\u05D9\u05DD, \u05D8\u05D9\u05E7\u05E8", Icon: ScrollText, category: "elements", scope: "all" },
  /* ── Design ── */
  { type: "quote", label: "\u05E6\u05D9\u05D8\u05D5\u05D8", description: "\u05E6\u05D9\u05D8\u05D5\u05D8 \u05D0\u05D5 \u05D4\u05DE\u05DC\u05E6\u05D4", Icon: Quote, category: "design", scope: "all" },
  { type: "callout", label: "\u05D4\u05D3\u05D2\u05E9\u05D4", description: "\u05EA\u05D9\u05D1\u05EA \u05DE\u05D9\u05D3\u05E2 / \u05D0\u05D6\u05D4\u05E8\u05D4", Icon: Info, category: "design", scope: "all" },
  { type: "divider", label: "\u05DE\u05E4\u05E8\u05D9\u05D3", description: "\u05E7\u05D5 \u05DE\u05E4\u05E8\u05D9\u05D3 \u05D0\u05E0\u05DB\u05D9 \u05DE\u05EA\u05DB\u05D5\u05D5\u05E0\u05DF", Icon: Minus, category: "design", scope: "all" },
  { type: "spacer", label: "\u05E8\u05D5\u05D5\u05D7", description: "\u05E8\u05D5\u05D5\u05D7 \u05D0\u05E0\u05DB\u05D9 \u05DE\u05EA\u05DB\u05D5\u05D5\u05E0\u05DF", Icon: MoveVertical, category: "design", scope: "all" },
  /* ── Sections: Branding ── */
  { type: "sectionSplitContent", label: "\u05D8\u05E7\u05E1\u05D8 + \u05EA\u05DE\u05D5\u05E0\u05D4", description: "\u05E9\u05E0\u05D9 \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD: \u05EA\u05D5\u05DB\u05DF \u05D1\u05E6\u05D3 \u05D0\u05D7\u05D3 \u05D5\u05EA\u05DE\u05D5\u05E0\u05D4 \u05D1\u05E6\u05D3 \u05D4\u05E9\u05E0\u05D9", Icon: Columns2, category: "sections", scope: "all" },
  { type: "sectionTestimonials", label: "\u05D4\u05DE\u05DC\u05E6\u05D5\u05EA", description: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05D4\u05DE\u05DC\u05E6\u05D5\u05EA \u05E2\u05DD \u05E6\u05D9\u05D8\u05D5\u05D8, \u05E9\u05DD \u05D5\u05D3\u05D9\u05E8\u05D5\u05D2 \u05DB\u05D5\u05DB\u05D1\u05D9\u05DD", Icon: MessageSquareQuote, category: "sections", scope: "all" },
  { type: "sectionLogos", label: "\u05DC\u05D5\u05D2\u05D5\u05D0\u05D9\u05DD / \u05DC\u05E7\u05D5\u05D7\u05D5\u05EA", description: "\u05E9\u05D5\u05E8\u05EA \u05DC\u05D5\u05D2\u05D5\u05D0\u05D9\u05DD \u05E9\u05DC \u05DC\u05E7\u05D5\u05D7\u05D5\u05EA \u05D0\u05D5 \u05E9\u05D5\u05EA\u05E4\u05D9\u05DD", Icon: Handshake, category: "sections", scope: "all" },
  { type: "sectionContactForm", label: "\u05D8\u05D5\u05E4\u05E1 \u05D9\u05E6\u05D9\u05E8\u05EA \u05E7\u05E9\u05E8", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + \u05E9\u05D3\u05D5\u05EA \u05D8\u05D5\u05E4\u05E1 + \u05DB\u05E4\u05EA\u05D5\u05E8 \u05E9\u05DC\u05D9\u05D7\u05D4", Icon: Mail, category: "sections", scope: "all" },
  { type: "sectionBenefits", label: "\u05D9\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05D9\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA \u05E2\u05DD \u05E1\u05D9\u05DE\u05E0\u05D9 V", Icon: Award, category: "sections", scope: "all" },
  { type: "sectionNumberedCards", label: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05DD \u05DE\u05DE\u05D5\u05E1\u05E4\u05E8\u05D9\u05DD", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + \u05E8\u05E9\u05EA 2x2 \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9\u05DD \u05DE\u05DE\u05D5\u05E1\u05E4\u05E8\u05D9\u05DD", Icon: ListOrdered, category: "sections", scope: "all" },
  { type: "sectionFeatureCards", label: "\u05EA\u05DB\u05D5\u05E0\u05D5\u05EA \u05E2\u05DD \u05D0\u05D9\u05D9\u05E7\u05D5\u05DF", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + \u05E8\u05E9\u05EA 2x2 \u05EA\u05DB\u05D5\u05E0\u05D5\u05EA \u05E2\u05DD \u05E1\u05D9\u05DE\u05E0\u05D9 V", Icon: ShieldCheck, category: "sections", scope: "all" },
  { type: "sectionServiceCards", label: "\u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05E9\u05D9\u05E8\u05D5\u05EA\u05D9\u05DD", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + \u05E9\u05D5\u05E8\u05EA \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05E9\u05D9\u05E8\u05D5\u05EA \u05DB\u05D4\u05D9\u05DD", Icon: Layers, category: "sections", scope: "all" },
  { type: "sectionBlogPosts", label: "\u05E4\u05D5\u05E1\u05D8\u05D9\u05DD \u05DE\u05D4\u05D1\u05DC\u05D5\u05D2", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + 3 \u05DB\u05E8\u05D8\u05D9\u05E1\u05D9 \u05E4\u05D5\u05E1\u05D8\u05D9\u05DD + \u05DB\u05E4\u05EA\u05D5\u05E8 \u05DC\u05E2\u05DE\u05D5\u05D3 \u05D4\u05D1\u05DC\u05D5\u05D2", Icon: Newspaper, category: "sections", scope: "all" },
  { type: "sectionProcessSteps", label: "\u05EA\u05D4\u05DC\u05D9\u05DA \u05E2\u05D1\u05D5\u05D3\u05D4", description: "\u05DB\u05D5\u05EA\u05E8\u05EA + \u05E9\u05DC\u05D1\u05D9\u05DD \u05DE\u05DE\u05D5\u05E1\u05E4\u05E8\u05D9\u05DD \u05E2\u05DD \u05DB\u05D5\u05EA\u05E8\u05EA \u05D5\u05EA\u05D9\u05D0\u05D5\u05E8", Icon: ListOrdered, category: "sections", scope: "all" },
  /* ── Sections: Store ── */
  { type: "sectionFeaturedProducts", label: "\u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05DE\u05D5\u05DE\u05DC\u05E6\u05D9\u05DD", description: "\u05E8\u05E9\u05EA \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05DE\u05D5\u05DE\u05DC\u05E6\u05D9\u05DD \u05E2\u05DD \u05EA\u05D9\u05D0\u05D5\u05E8 \u05D5\u05DE\u05D7\u05D9\u05E8", Icon: ShoppingBag, category: "sections", scope: "store" },
  { type: "sectionProductCategories", label: "\u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD", description: "\u05E8\u05E9\u05EA \u05E7\u05D8\u05D2\u05D5\u05E8\u05D9\u05D5\u05EA \u05DE\u05D5\u05E6\u05E8\u05D9\u05DD \u05E2\u05DD \u05EA\u05D9\u05D0\u05D5\u05E8 \u05D5\u05EA\u05DE\u05D5\u05E0\u05D4", Icon: Grid3X3, category: "sections", scope: "store" },
  { type: "sectionPromoBanner", label: "\u05D1\u05D0\u05E0\u05E8 \u05DE\u05D1\u05E6\u05E2", description: "\u05D1\u05D0\u05E0\u05E8 \u05E6\u05D1\u05E2\u05D5\u05E0\u05D9 \u05E2\u05DD \u05D8\u05E7\u05E1\u05D8, \u05D4\u05E0\u05D7\u05D4 \u05D5\u05DB\u05E4\u05EA\u05D5\u05E8 CTA", Icon: Megaphone, category: "sections", scope: "store" },
  { type: "sectionStoreBenefits", label: "\u05E1\u05E8\u05D2\u05DC \u05D9\u05EA\u05E8\u05D5\u05E0\u05D5\u05EA \u05D7\u05E0\u05D5\u05EA", description: "\u05E9\u05D5\u05E8\u05EA \u05D0\u05D9\u05D9\u05E7\u05D5\u05E0\u05D9\u05DD: \u05DE\u05E9\u05DC\u05D5\u05D7, \u05D0\u05D7\u05E8\u05D9\u05D5\u05EA, \u05D4\u05D7\u05D6\u05E8\u05D5\u05EA, \u05EA\u05E9\u05DC\u05D5\u05DD \u05DE\u05D0\u05D5\u05D1\u05D8\u05D7", Icon: Truck, category: "sections", scope: "store" },
  { type: "sectionFAQ", label: "\u05E9\u05D0\u05DC\u05D5\u05EA \u05E0\u05E4\u05D5\u05E6\u05D5\u05EA", description: "\u05E8\u05E9\u05D9\u05DE\u05EA \u05E9\u05D0\u05DC\u05D5\u05EA \u05D5\u05EA\u05E9\u05D5\u05D1\u05D5\u05EA \u05D1\u05D0\u05E7\u05D5\u05E8\u05D3\u05D9\u05D5\u05DF", Icon: HelpCircle, category: "sections", scope: "store" },
];

/** Get blocks filtered by template type */
export function getBlocksForType(templateType: "branding" | "store"): PaletteBlockType[] {
  return ALL_BLOCK_TYPES.filter(
    (b) => b.scope === "all" || b.scope === templateType
  );
}

const CATEGORIES = [
  { key: "saved" as const, label: "\u05E9\u05DE\u05D5\u05E8\u05D9\u05DD", emoji: "\uD83D\uDD16" },
  { key: "pages" as const, label: "\u05E2\u05DE\u05D5\u05D3\u05D9\u05DD", emoji: "\uD83D\uDCC4" },
  { key: "sections" as const, label: "\u05E1\u05E7\u05E9\u05E0\u05D9\u05DD", emoji: "\uD83C\uDFD7\uFE0F" },
  { key: "content" as const, label: "\u05EA\u05D5\u05DB\u05DF", emoji: "\uD83D\uDCDD" },
  { key: "media" as const, label: "\u05DE\u05D3\u05D9\u05D4", emoji: "\uD83C\uDFA8" },
  { key: "elements" as const, label: "\u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD", emoji: "\uD83E\uDDE9" },
  { key: "design" as const, label: "\u05E2\u05D9\u05E6\u05D5\u05D1", emoji: "\u2728" },
];

/* ────── Saved Sections Context ────── */

interface SavedSectionsCtx {
  savedSections: SavedSection[];
  refresh: () => void;
  isLoading: boolean;
}

const SavedSectionsContext = createContext<SavedSectionsCtx>({
  savedSections: [],
  refresh: () => {},
  isLoading: false,
});

export function useSavedSections() {
  return useContext(SavedSectionsContext);
}

/* ────── Page Templates Context ────── */

interface PageTemplatesCtx {
  pageTemplates: PageTemplate[];
  refresh: () => void;
  isLoading: boolean;
}

const PageTemplatesContext = createContext<PageTemplatesCtx>({
  pageTemplates: [],
  refresh: () => {},
  isLoading: false,
});

export function usePageTemplates() {
  return useContext(PageTemplatesContext);
}

/* ────── Hook: useIsDraggingFromPalette ────── */
export function useIsDraggingAny() {
  const { isDragging, itemType } = useDragLayer((monitor) => ({
    isDragging: monitor.isDragging(),
    itemType: monitor.getItemType(),
  }));
  return { isDragging, isPaletteDrag: isDragging && itemType === DND_PALETTE_BLOCK };
}

/* ────── Search helper ────── */
function normalizeSearch(s: string): string {
  return s.trim().toLowerCase();
}

function matchesSearch(block: PaletteBlockType, query: string): boolean {
  if (!query) return true;
  const q = normalizeSearch(query);
  return (
    block.label.toLowerCase().includes(q) ||
    block.description.toLowerCase().includes(q) ||
    block.type.toLowerCase().includes(q)
  );
}

/* ────── Helper: collect all blocks for a page from overrides ────── */

interface ZoneBlocks {
  zoneId: string;
  blocks: any[];
}

/**
 * Scans getAllOverrides() for all block keys matching `blocks:{pageKey}:*`
 * and returns them grouped by zone, preserving their order.
 * This works regardless of zone names (bottom, in-hero, after-hero, etc.)
 */
function collectPageBlocks(
  getAllOverrides: () => Record<string, string>,
  getContent: (key: string, def: string) => string,
  pageKey: string,
): ZoneBlocks[] {
  const prefix = `blocks:${pageKey}:`;
  const allOverrides = getAllOverrides();
  const zones: ZoneBlocks[] = [];

  for (const key of Object.keys(allOverrides)) {
    if (!key.startsWith(prefix)) continue;
    const zoneId = key.slice(prefix.length);
    try {
      const raw = getContent(key, "[]");
      const blocks = JSON.parse(raw);
      if (Array.isArray(blocks) && blocks.length > 0) {
        zones.push({ zoneId, blocks });
      }
    } catch {
      // skip malformed
    }
  }

  return zones;
}

/**
 * Flatten all zones into a single blocks array (stripped of IDs) for saving.
 * Preserves zone info as metadata so it can be restored when loading.
 */
function flattenZonesToTemplateBlocks(zones: ZoneBlocks[]): PageTemplateBlock[] {
  const result: PageTemplateBlock[] = [];
  for (const zone of zones) {
    for (const b of zone.blocks) {
      result.push({
        type: b.type,
        content: b.content,
        variant: b.variant,
        meta: b.meta ? JSON.parse(JSON.stringify(b.meta)) : undefined,
        _zoneId: zone.zoneId,
      } as any);
    }
  }
  return result;
}

/* ────── Draggable Palette Item ────── */

function PaletteItem({ block }: { block: PaletteBlockType }) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DND_PALETTE_BLOCK,
      item: { blockType: block.type },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [block.type]
  );

  const IconComp = block.Icon;
  const isSection = block.category === "sections";
  const isStore = block.scope === "store";

  return (
    <div
      ref={drag}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-grab active:cursor-grabbing transition-all select-none ${
        isDragging
          ? "opacity-40 scale-95 ring-2 ring-zap/40 bg-zap-pale"
          : isSection && isStore
          ? "hover:bg-purple-50 hover:shadow-sm border border-transparent hover:border-purple-200"
          : isSection
          ? "hover:bg-amber-50 hover:shadow-sm border border-transparent hover:border-amber-200"
          : "hover:bg-blue-50 hover:shadow-sm"
      }`}
    >
      <div className={`w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border ${
        isSection && isStore ? "bg-purple-50 border-purple-200" : isSection ? "bg-amber-50 border-amber-200" : "bg-zap-pale border-zap-pale"
      }`}>
        <IconComp className={`w-4 h-4 ${isSection && isStore ? "text-purple-600" : isSection ? "text-amber-600" : "text-zap"}`} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zap-navy leading-tight" style={{ fontWeight: 600 }}>
          {block.label}
        </p>
        <p className="text-[11px] text-gray-400 leading-tight mt-0.5">{block.description}</p>
      </div>
      <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0" />
    </div>
  );
}

/* ────── Draggable Saved Section Item ────── */

function SavedPaletteItem({ section, onDeleted }: { section: SavedSection; onDeleted: () => void }) {
  const [{ isDragging }, drag] = useDrag(
    () => ({
      type: DND_PALETTE_BLOCK,
      item: {
        blockType: section.blockType,
        savedContent: section.content,
        savedMeta: section.meta,
        savedVariant: section.variant,
      },
      collect: (monitor) => ({ isDragging: monitor.isDragging() }),
    }),
    [section]
  );

  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteSavedSection(section.id);
      onDeleted();
    } catch (err) {
      console.error("[SavedPaletteItem] delete error:", err);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const builtIn = ALL_BLOCK_TYPES.find((b) => b.type === section.blockType);
  const IconComp = builtIn?.Icon || Bookmark;

  const dateStr = new Date(section.savedAt).toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
  });

  return (
    <div
      ref={drag}
      className={`flex items-center gap-3 px-3 py-2.5 rounded-lg cursor-grab active:cursor-grabbing transition-all select-none group/saved ${
        isDragging
          ? "opacity-40 scale-95 ring-2 ring-emerald-400/40 bg-emerald-50"
          : "hover:bg-emerald-50 hover:shadow-sm border border-transparent hover:border-emerald-200"
      }`}
    >
      <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border bg-emerald-50 border-emerald-200">
        <IconComp className="w-4 h-4 text-emerald-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm text-zap-navy leading-tight" style={{ fontWeight: 600 }}>
          {section.name}
        </p>
        <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
          {section.description || (builtIn?.label || section.blockType)}
          {" \u00B7 "}
          {dateStr}
        </p>
      </div>

      {/* Delete button */}
      <div className="shrink-0 relative">
        {showConfirm ? (
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); handleDelete(); }}
              disabled={deleting}
              className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
              style={{ fontWeight: 600 }}
            >
              {deleting ? "..." : "\u05DE\u05D7\u05E7"}
            </button>
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
              className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
            >
              \u00D7
            </button>
          </div>
        ) : (
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
            className="w-6 h-6 rounded flex items-center justify-center text-gray-300 opacity-0 group-hover/saved:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all"
            title={"\u05DE\u05D7\u05E7 \u05DE\u05D4\u05E1\u05E4\u05E8\u05D9\u05D9\u05D4"}
          >
            <Trash2 className="w-3 h-3" />
          </button>
        )}
      </div>

      <GripVertical className="w-3.5 h-3.5 text-gray-300 shrink-0" />
    </div>
  );
}

/* ────── Page Template Item ────── */

function PageTemplateItem({
  template,
  onDeleted,
  onLoadToCurrentPage,
}: {
  template: PageTemplate;
  onDeleted: () => void;
  onLoadToCurrentPage: (template: PageTemplate, mode: "append" | "replace") => void;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [showActions, setShowActions] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deletePageTemplate(template.id);
      onDeleted();
    } catch (err) {
      console.error("[PageTemplateItem] delete error:", err);
    } finally {
      setDeleting(false);
      setShowConfirm(false);
    }
  };

  const dateStr = new Date(template.createdAt).toLocaleDateString("he-IL", {
    day: "numeric",
    month: "short",
  });

  const typeSummary = template.blockSummary || `${template.blockCount} \u05D1\u05DC\u05D5\u05E7\u05D9\u05DD`;

  /** Zone count info */
  const zoneCount = template.zones ? template.zones.length : 0;

  return (
    <div className="group/ptpl">
      <div
        className="flex items-center gap-3 px-3 py-2.5 rounded-lg select-none cursor-pointer hover:bg-sky-50 hover:shadow-sm border border-transparent hover:border-sky-200"
        onClick={() => setShowActions(!showActions)}
      >
        <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0 border bg-sky-50 border-sky-200">
          <FileText className="w-4 h-4 text-sky-600" />
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm text-zap-navy leading-tight" style={{ fontWeight: 600 }}>
            {template.name}
          </p>
          <p className="text-[11px] text-gray-400 leading-tight mt-0.5">
            {template.blockCount} {"\u05D1\u05DC\u05D5\u05E7\u05D9\u05DD"}
            {zoneCount > 1 && ` \u00B7 ${zoneCount} \u05D0\u05D6\u05D5\u05E8\u05D9\u05DD`}
            {" \u00B7 "}
            {dateStr}
          </p>
          {template.description && (
            <p className="text-[10px] text-gray-400 leading-tight mt-0.5 truncate">
              {template.description}
            </p>
          )}
        </div>

        {/* Delete button */}
        <div className="shrink-0 relative">
          {showConfirm ? (
            <div className="flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); handleDelete(); }}
                disabled={deleting}
                className="text-[10px] px-1.5 py-0.5 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                style={{ fontWeight: 600 }}
              >
                {deleting ? "..." : "\u05DE\u05D7\u05E7"}
              </button>
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setShowConfirm(false); }}
                className="text-[10px] px-1.5 py-0.5 bg-gray-200 text-gray-600 rounded hover:bg-gray-300 transition-colors"
              >
                \u00D7
              </button>
            </div>
          ) : (
            <button
              type="button"
              onClick={(e) => { e.stopPropagation(); setShowConfirm(true); }}
              className="w-6 h-6 rounded flex items-center justify-center text-gray-300 opacity-0 group-hover/ptpl:opacity-100 hover:text-red-500 hover:bg-red-50 transition-all"
              title={"\u05DE\u05D7\u05E7 \u05EA\u05D1\u05E0\u05D9\u05EA \u05E2\u05DE\u05D5\u05D3"}
            >
              <Trash2 className="w-3 h-3" />
            </button>
          )}
        </div>
      </div>

      {/* Action buttons when expanded */}
      <AnimatePresence>
        {showActions && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.15 }}
            className="overflow-hidden"
          >
            <div className="flex gap-2 px-3 pb-2.5 pt-1 mr-12">
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onLoadToCurrentPage(template, "append");
                  setShowActions(false);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                style={{ fontWeight: 600 }}
              >
                <Plus className="w-3 h-3" />
                {"\u05D4\u05D5\u05E1\u05E3 \u05DC\u05E2\u05DE\u05D5\u05D3"}
              </button>
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  onLoadToCurrentPage(template, "replace");
                  setShowActions(false);
                }}
                className="flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 text-[11px] bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors"
                style={{ fontWeight: 600 }}
              >
                <Replace className="w-3 h-3" />
                {"\u05D4\u05D7\u05DC\u05E3 \u05E2\u05DE\u05D5\u05D3"}
              </button>
            </div>
            {/* Summary of block types */}
            {template.blockSummary && (
              <div className="px-3 pb-2 mr-12">
                <p className="text-[10px] text-sky-600 bg-sky-50 px-2 py-1 rounded">
                  {template.blockSummary}
                </p>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

/* ────── Save Page Dialog ────── */

function SavePageDialog({
  isOpen,
  onClose,
  onSaved,
}: {
  isOpen: boolean;
  onClose: () => void;
  onSaved: () => void;
}) {
  const ctx = useContentEditingSafe();
  const relativePath = useRelativePath();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const nameInputRef = useRef<HTMLInputElement>(null);

  const pageKey = relativePath === "/" ? "home" : relativePath.replace(/^\//, "");

  /**
   * Collect all blocks for the current page across ALL zones.
   * Uses getAllOverrides() to find every `blocks:{pageKey}:*` key.
   */
  const pageZones = useMemo((): ZoneBlocks[] => {
    if (!isOpen || !ctx) return [];
    return collectPageBlocks(ctx.getAllOverrides, ctx.getContent, pageKey);
  }, [isOpen, ctx, pageKey]);

  const totalBlockCount = useMemo(
    () => pageZones.reduce((sum, z) => sum + z.blocks.length, 0),
    [pageZones],
  );

  const templateBlocks = useMemo(
    () => flattenZonesToTemplateBlocks(pageZones),
    [pageZones],
  );

  // Build block summary
  const blockSummary = useMemo(() => {
    if (templateBlocks.length === 0) return "";
    const typeCounts: Record<string, number> = {};
    for (const b of templateBlocks) {
      const label = ALL_BLOCK_TYPES.find((bt) => bt.type === b.type)?.label || b.type;
      typeCounts[label] = (typeCounts[label] || 0) + 1;
    }
    const parts = Object.entries(typeCounts)
      .slice(0, 5)
      .map(([label, count]) => count > 1 ? `${label} \u00D7${count}` : label);
    if (Object.keys(typeCounts).length > 5) parts.push("...");
    return parts.join(", ");
  }, [templateBlocks]);

  useEffect(() => {
    if (isOpen) {
      setName("");
      setDescription("");
      setError("");
      setTimeout(() => nameInputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSave = async () => {
    if (!name.trim()) {
      setError("\u05E0\u05D0 \u05DC\u05D4\u05D6\u05D9\u05DF \u05E9\u05DD \u05DC\u05EA\u05D1\u05E0\u05D9\u05EA");
      return;
    }
    if (totalBlockCount === 0) {
      setError("\u05D0\u05D9\u05DF \u05D1\u05DC\u05D5\u05E7\u05D9\u05DD \u05D1\u05E2\u05DE\u05D5\u05D3 \u05D4\u05E0\u05D5\u05DB\u05D7\u05D9");
      return;
    }
    setSaving(true);
    setError("");
    try {
      await savePageTemplate({
        name: name.trim(),
        description: description.trim(),
        blocks: templateBlocks,
        blockCount: totalBlockCount,
        blockSummary,
        zones: pageZones.map((z) => ({
          zoneId: z.zoneId,
          blockCount: z.blocks.length,
        })),
      } as any);
      onSaved();
      onClose();
    } catch (err) {
      console.error("[SavePageDialog] save error:", err);
      setError("\u05E9\u05D2\u05D9\u05D0\u05D4 \u05D1\u05E9\u05DE\u05D9\u05E8\u05D4");
    } finally {
      setSaving(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl w-[400px] max-w-[90vw] p-5"
        dir="rtl"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-2 mb-4">
          <div className="w-8 h-8 rounded-lg bg-sky-100 flex items-center justify-center">
            <Save className="w-4 h-4 text-sky-600" />
          </div>
          <div>
            <h3 className="text-sm text-zap-navy" style={{ fontWeight: 700 }}>
              {"\u05E9\u05DE\u05D5\u05E8 \u05E2\u05DE\u05D5\u05D3 \u05DB\u05EA\u05D1\u05E0\u05D9\u05EA"}
            </h3>
            <p className="text-[11px] text-gray-400">
              {"\u05E2\u05DE\u05D5\u05D3: "}{pageKey}{" \u00B7 "}
              {totalBlockCount} {"\u05D1\u05DC\u05D5\u05E7\u05D9\u05DD"}
              {pageZones.length > 1 && ` \u05D1-${pageZones.length} \u05D0\u05D6\u05D5\u05E8\u05D9\u05DD`}
            </p>
          </div>
        </div>

        {/* Zone breakdown */}
        {pageZones.length > 0 && (
          <div className="mb-3 px-3 py-2 bg-sky-50 rounded-lg space-y-1">
            {blockSummary && (
              <p className="text-[11px] text-sky-700" style={{ fontWeight: 500 }}>
                {blockSummary}
              </p>
            )}
            {pageZones.length > 1 && (
              <div className="text-[10px] text-sky-600 space-y-0.5">
                {pageZones.map((z) => (
                  <div key={z.zoneId} className="flex justify-between">
                    <span>{z.zoneId}</span>
                    <span>{z.blocks.length} {"\u05D1\u05DC\u05D5\u05E7\u05D9\u05DD"}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {totalBlockCount === 0 && (
          <div className="mb-3 px-3 py-3 bg-orange-50 rounded-lg text-center">
            <p className="text-[12px] text-orange-600" style={{ fontWeight: 500 }}>
              {"\u05D0\u05D9\u05DF \u05D1\u05DC\u05D5\u05E7\u05D9\u05DD \u05D1\u05E2\u05DE\u05D5\u05D3 \u05D4\u05E0\u05D5\u05DB\u05D7\u05D9"}
            </p>
            <p className="text-[10px] text-orange-500 mt-1">
              {"\u05D4\u05D5\u05E1\u05D9\u05E4\u05D5 \u05D1\u05DC\u05D5\u05E7\u05D9\u05DD \u05DC\u05E2\u05DE\u05D5\u05D3 \u05D3\u05E8\u05DA \u05D1\u05E0\u05E7 \u05D4\u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD \u05D0\u05D5 \u05D2\u05E8\u05D9\u05E8\u05D4 \u05DC\u05EA\u05D5\u05DA \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD"}
            </p>
          </div>
        )}

        <div className="space-y-3">
          <div>
            <label className="text-xs text-gray-500 mb-1 block" style={{ fontWeight: 500 }}>
              {"\u05E9\u05DD \u05D4\u05EA\u05D1\u05E0\u05D9\u05EA"}
            </label>
            <input
              ref={nameInputRef}
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              placeholder={"\u05DC\u05DE\u05E9\u05DC: \u05E2\u05DE\u05D5\u05D3 \u05D0\u05D5\u05D3\u05D5\u05EA"}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 placeholder:text-gray-400 focus:outline-none focus:border-sky-400 focus:bg-white"
              dir="rtl"
            />
          </div>
          <div>
            <label className="text-xs text-gray-500 mb-1 block" style={{ fontWeight: 500 }}>
              {"\u05EA\u05D9\u05D0\u05D5\u05E8 (\u05D0\u05D5\u05E4\u05E6\u05D9\u05D5\u05E0\u05DC\u05D9)"}
            </label>
            <input
              type="text"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSave(); }}
              placeholder={"\u05DC\u05DE\u05E9\u05DC: \u05E2\u05DE\u05D5\u05D3 \u05E1\u05D8\u05E0\u05D3\u05E8\u05D8\u05D9 \u05E2\u05DD \u05D4\u05D9\u05E8\u05D5 \u05D5\u05D8\u05D5\u05E4\u05E1"}
              className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 placeholder:text-gray-400 focus:outline-none focus:border-sky-400 focus:bg-white"
              dir="rtl"
            />
          </div>
        </div>

        {error && (
          <p className="text-xs text-red-500 mt-2" style={{ fontWeight: 500 }}>{error}</p>
        )}

        <div className="flex gap-2 mt-4">
          <button
            type="button"
            onClick={handleSave}
            disabled={saving || totalBlockCount === 0}
            className="flex-1 flex items-center justify-center gap-1.5 px-4 py-2 text-sm bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors disabled:opacity-50"
            style={{ fontWeight: 600 }}
          >
            {saving ? (
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
            ) : (
              <Save className="w-3.5 h-3.5" />
            )}
            {saving ? "\u05E9\u05D5\u05DE\u05E8..." : "\u05E9\u05DE\u05D5\u05E8"}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-500 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
            style={{ fontWeight: 500 }}
          >
            {"\u05D1\u05D9\u05D8\u05D5\u05DC"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ────── Main Palette ────── */

export function BlockPalette() {
  const ctx = useContentEditingSafe();
  const isEditMode = ctx?.isEditMode ?? false;
  const templateType = useTemplateType();
  const relativePath = useRelativePath();
  const [isOpen, setIsOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const panelRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  /* ── Saved sections state ── */
  const [savedSections, setSavedSections] = useState<SavedSection[]>([]);
  const [savedLoading, setSavedLoading] = useState(false);

  const loadSaved = useCallback(async () => {
    setSavedLoading(true);
    try {
      const sections = await listSavedSections();
      setSavedSections(sections);
    } catch (err) {
      console.error("[BlockPalette] Failed to load saved sections:", err);
    } finally {
      setSavedLoading(false);
    }
  }, []);

  /* ── Page templates state ── */
  const [pageTemplates, setPageTemplates] = useState<PageTemplate[]>([]);
  const [pagesLoading, setPagesLoading] = useState(false);
  const [showSavePageDialog, setShowSavePageDialog] = useState(false);

  const loadPageTemplates = useCallback(async () => {
    setPagesLoading(true);
    try {
      const templates = await listPageTemplates();
      setPageTemplates(templates);
    } catch (err) {
      console.error("[BlockPalette] Failed to load page templates:", err);
    } finally {
      setPagesLoading(false);
    }
  }, []);

  // Load saved sections + page templates when palette opens
  useEffect(() => {
    if (isOpen && isEditMode) {
      loadSaved();
      loadPageTemplates();
    }
  }, [isOpen, isEditMode, loadSaved, loadPageTemplates]);

  const visibleBlocks = getBlocksForType(templateType);

  const filteredBlocks = useMemo(() => {
    if (!searchQuery.trim()) return visibleBlocks;
    return visibleBlocks.filter((b) => matchesSearch(b, searchQuery));
  }, [visibleBlocks, searchQuery]);

  const filteredSaved = useMemo(() => {
    if (!searchQuery.trim()) return savedSections;
    const q = normalizeSearch(searchQuery);
    return savedSections.filter(
      (s) =>
        s.name.toLowerCase().includes(q) ||
        s.description.toLowerCase().includes(q) ||
        s.blockType.toLowerCase().includes(q)
    );
  }, [savedSections, searchQuery]);

  const filteredPageTemplates = useMemo(() => {
    if (!searchQuery.trim()) return pageTemplates;
    const q = normalizeSearch(searchQuery);
    return pageTemplates.filter(
      (t) =>
        t.name.toLowerCase().includes(q) ||
        t.description.toLowerCase().includes(q) ||
        (t.blockSummary || "").toLowerCase().includes(q)
    );
  }, [pageTemplates, searchQuery]);

  // Reset palette to closed whenever edit mode is toggled off
  useEffect(() => {
    if (!isEditMode) {
      setIsOpen(false);
      setSearchQuery("");
    }
  }, [isEditMode]);

  // Focus search when palette opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => searchInputRef.current?.focus(), 200);
    } else {
      setSearchQuery("");
    }
  }, [isOpen]);

  // Escape key closes the palette
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isOpen]);

  // Click outside closes the palette
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (panelRef.current && !panelRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    const timer = setTimeout(() => {
      document.addEventListener("mousedown", handleClickOutside);
    }, 100);
    return () => {
      clearTimeout(timer);
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  /**
   * Load page template blocks into the current page.
   *
   * Zone IDs are page-specific (e.g. "services-after-hero",
   * "service-b-after-advantages"). When loading a template saved from
   * page A into page B, those zones don't exist on B.
   *
   * Strategy:
   * 1. Discover which zones already exist on the TARGET page (from overrides)
   * 2. If a template block's original zone exists on target, keep it
   * 3. Unmatched blocks go into the first available zone on the target page
   * 4. If no zones exist on target yet, use "in-hero" (every page has it)
   */
  const handleLoadPageTemplate = useCallback((template: PageTemplate, mode: "append" | "replace") => {
    if (!ctx) return;
    const pageKey = relativePath === "/" ? "home" : relativePath.replace(/^\//, "");

    // Discover which zones already exist on the TARGET page
    const allOverrides = ctx.getAllOverrides();
    const targetPrefix = `blocks:${pageKey}:`;
    const targetZoneIds: string[] = [];
    for (const key of Object.keys(allOverrides)) {
      if (key.startsWith(targetPrefix)) {
        targetZoneIds.push(key.slice(targetPrefix.length));
      }
    }

    // Fallback: first existing zone, or "in-hero" which every page has
    const fallbackZone = targetZoneIds.length > 0 ? targetZoneIds[0] : "in-hero";

    // Route template blocks to matching zones, or fallback
    const zoneMap: Record<string, any[]> = {};
    for (const b of template.blocks) {
      const originalZone = (b as any)._zoneId || fallbackZone;
      const targetZone = targetZoneIds.includes(originalZone) ? originalZone : fallbackZone;
      if (!zoneMap[targetZone]) zoneMap[targetZone] = [];
      zoneMap[targetZone].push(b);
    }

    let totalAdded = 0;

    for (const [zoneId, tplBlocks] of Object.entries(zoneMap)) {
      const storageKey = `blocks:${pageKey}:${zoneId}`;

      let existing: any[] = [];
      if (mode === "append") {
        try {
          existing = JSON.parse(ctx.getContent(storageKey, "[]"));
          if (!Array.isArray(existing)) existing = [];
        } catch { existing = []; }
      }

      const newBlocks = tplBlocks.map((b: any) => ({
        id: `blk-${Date.now()}-${Math.random().toString(36).substring(2, 7)}`,
        type: b.type,
        content: b.content,
        variant: b.variant,
        meta: b.meta ? JSON.parse(JSON.stringify(b.meta)) : undefined,
      }));

      const merged = mode === "replace" ? newBlocks : [...existing, ...newBlocks];
      ctx.saveContent(storageKey, JSON.stringify(merged));
      totalAdded += newBlocks.length;
    }

    // Visual feedback
    const actionLabel = mode === "replace" ? "\u05D4\u05D5\u05D7\u05DC\u05E4\u05D5" : "\u05E0\u05D5\u05E1\u05E4\u05D5";
    const el = document.createElement("div");
    el.textContent = `\u05E0\u05D8\u05E2\u05E0\u05D5! ${totalAdded} \u05D1\u05DC\u05D5\u05E7\u05D9\u05DD ${actionLabel} \u05DC\u05E2\u05DE\u05D5\u05D3`;
    el.style.cssText = "position:fixed;top:20px;left:50%;transform:translateX(-50%);z-index:9999;background:#0ea5e9;color:#fff;padding:8px 20px;border-radius:10px;font-size:13px;font-weight:600;box-shadow:0 4px 12px rgba(0,0,0,0.15);pointer-events:none;direction:rtl;";
    document.body.appendChild(el);
    setTimeout(() => el.remove(), 2000);
  }, [ctx, relativePath]);

  if (!isEditMode) return null;

  const isStore = templateType === "store";
  const totalItems = filteredBlocks.length + filteredSaved.length + filteredPageTemplates.length;

  return (
    <SavedSectionsContext.Provider value={{ savedSections, refresh: loadSaved, isLoading: savedLoading }}>
      <PageTemplatesContext.Provider value={{ pageTemplates, refresh: loadPageTemplates, isLoading: pagesLoading }}>
        {/* Toggle Button */}
        <AnimatePresence>
          {!isOpen && (
            <motion.button
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.2 }}
              data-tour-step="palette-btn"
              onClick={() => setIsOpen(true)}
              className="fixed top-16 left-6 z-[998] flex items-center gap-2 px-4 py-2.5 bg-zap-navy text-white rounded-xl shadow-lg hover:bg-zap-dark transition-colors"
              style={{ fontWeight: 500 }}
              title={"\u05E4\u05EA\u05D7 \u05D1\u05E0\u05E7 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD (Esc \u05DC\u05E1\u05D2\u05D9\u05E8\u05D4)"}
            >
              <Package className="w-4 h-4" />
              <span className="text-sm">{"\u05D1\u05E0\u05E7 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD"}</span>
              {(savedSections.length > 0 || pageTemplates.length > 0) && (
                <span className="text-[10px] bg-emerald-400 text-white rounded-full px-1.5 py-0.5" style={{ fontWeight: 700 }}>
                  {savedSections.length + pageTemplates.length}
                </span>
              )}
            </motion.button>
          )}
        </AnimatePresence>

        {/* Panel */}
        <AnimatePresence>
          {isOpen && (
            <motion.div
              ref={panelRef}
              initial={{ x: -320, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              exit={{ x: -320, opacity: 0 }}
              transition={{ type: "spring", damping: 25, stiffness: 300 }}
              className="fixed top-0 left-0 bottom-0 z-[998] w-[300px] bg-white border-r border-gray-200 shadow-2xl flex flex-col"
              dir="rtl"
            >
              {/* Header */}
              <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 bg-gray-50/50">
                <div className="flex items-center gap-2">
                  <Package className="w-4 h-4 text-zap" />
                  <h3 className="text-sm text-zap-navy" style={{ fontWeight: 700 }}>
                    {"\u05D1\u05E0\u05E7 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD"}
                  </h3>
                  <span className="text-[10px] text-gray-400 bg-gray-100 rounded-full px-2 py-0.5">
                    {totalItems}
                  </span>
                  {isStore && (
                    <span className="text-[10px] text-purple-600 bg-purple-100 rounded-full px-2 py-0.5" style={{ fontWeight: 600 }}>
                      {"\u05D7\u05E0\u05D5\u05EA"}
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setIsOpen(false)}
                  className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-gray-500 hover:text-red-500 hover:bg-red-50 transition-colors text-xs"
                  title={"\u05E1\u05D2\u05D5\u05E8 (Esc)"}
                  style={{ fontWeight: 500 }}
                >
                  <PanelLeftClose className="w-4 h-4" />
                  <span>{"\u05E1\u05D2\u05D5\u05E8"}</span>
                </button>
              </div>

              {/* Search bar */}
              <div className="px-3 py-2.5 border-b border-gray-100 bg-white">
                <div className="relative">
                  <Search className="absolute right-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-gray-400" />
                  <input
                    ref={searchInputRef}
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={"\u05D7\u05E4\u05E9\u05D5 \u05D0\u05DC\u05DE\u05E0\u05D8..."}
                    className="w-full pr-9 pl-3 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50 placeholder:text-gray-400 focus:outline-none focus:border-zap focus:bg-white transition-colors"
                    dir="rtl"
                  />
                  {searchQuery && (
                    <span
                      role="button"
                      tabIndex={0}
                      onClick={() => { setSearchQuery(""); searchInputRef.current?.focus(); }}
                      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { setSearchQuery(""); searchInputRef.current?.focus(); } }}
                      className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 cursor-pointer"
                    >
                      <X className="w-3.5 h-3.5" />
                    </span>
                  )}
                </div>
              </div>

              {/* Help text */}
              {!searchQuery && (
                <div className="px-4 py-2.5 bg-zap-pale/50 border-b border-zap-pale/50">
                  <p className="text-[11px] text-zap leading-relaxed" style={{ fontWeight: 500 }}>
                    {"\u05D2\u05E8\u05E8\u05D5 \u05D0\u05DC\u05DE\u05E0\u05D8 \u05D5\u05E9\u05D7\u05E8\u05E8\u05D5 \u05D1\u05D0\u05D7\u05D3 \u05DE\u05D0\u05D6\u05D5\u05E8\u05D9 \u05D4-Drop \u05D4\u05DB\u05D7\u05D5\u05DC\u05D9\u05DD \u05E9\u05DE\u05D5\u05E4\u05D9\u05E2\u05D9\u05DD \u05D1\u05EA\u05D5\u05DA \u05D4\u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05D5\u05D1\u05D9\u05E0\u05D4\u05DD"}
                  </p>
                </div>
              )}

              {/* Categories */}
              <div className="flex-1 overflow-y-auto">
                {totalItems === 0 && !searchQuery ? (
                  <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
                    <Search className="w-8 h-8 text-gray-300 mb-3" />
                    <p className="text-sm text-gray-500" style={{ fontWeight: 500 }}>
                      {"\u05DC\u05D0 \u05E0\u05DE\u05E6\u05D0\u05D5 \u05D0\u05DC\u05DE\u05E0\u05D8\u05D9\u05DD"}
                    </p>
                    <p className="text-xs text-gray-400 mt-1">
                      {"\u05E0\u05E1\u05D5 \u05DC\u05D7\u05E4\u05E9 \u05DE\u05D9\u05DC\u05D4 \u05D0\u05D7\u05E8\u05EA"}
                    </p>
                  </div>
                ) : searchQuery ? (
                  /* Flat list when searching */
                  <div className="p-2">
                    {filteredPageTemplates.map((t) => (
                      <PageTemplateItem
                        key={t.id}
                        template={t}
                        onDeleted={loadPageTemplates}
                        onLoadToCurrentPage={handleLoadPageTemplate}
                      />
                    ))}
                    {filteredSaved.map((s) => (
                      <SavedPaletteItem key={s.id} section={s} onDeleted={loadSaved} />
                    ))}
                    {filteredBlocks.map((block) => (
                      <PaletteItem key={block.type} block={block} />
                    ))}
                  </div>
                ) : (
                  /* Categorized when not searching */
                  CATEGORIES.map((cat) => {
                    /* ── Saved sections category ── */
                    if (cat.key === "saved") {
                      if (savedSections.length === 0 && !savedLoading) return null;
                      return (
                        <div key={cat.key} className="border-b border-gray-100">
                          <div className="px-4 py-2 bg-emerald-50/50 flex items-center justify-between">
                            <p className="text-xs text-emerald-700" style={{ fontWeight: 600 }}>
                              {cat.emoji} {cat.label}
                              <span className="text-[10px] text-emerald-500 mr-2" style={{ fontWeight: 400 }}>
                                {" \u2014 "}{savedSections.length}{" \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05E9\u05DE\u05D5\u05E8\u05D9\u05DD"}
                              </span>
                            </p>
                            <button
                              type="button"
                              onClick={loadSaved}
                              className="w-5 h-5 rounded flex items-center justify-center text-emerald-400 hover:text-emerald-600 hover:bg-emerald-100 transition-colors"
                              title={"\u05E8\u05E2\u05E0\u05DF"}
                            >
                              <RefreshCw className={`w-3 h-3 ${savedLoading ? "animate-spin" : ""}`} />
                            </button>
                          </div>
                          <div className="p-2">
                            {savedLoading && savedSections.length === 0 ? (
                              <p className="text-[11px] text-gray-400 text-center py-3">{"\u05D8\u05D5\u05E2\u05DF..."}</p>
                            ) : (
                              savedSections.map((s) => (
                                <SavedPaletteItem key={s.id} section={s} onDeleted={loadSaved} />
                              ))
                            )}
                          </div>
                        </div>
                      );
                    }

                    /* ── Page templates category ── */
                    if (cat.key === "pages") {
                      return (
                        <div key={cat.key} className="border-b border-gray-100">
                          <div className="px-4 py-2 bg-sky-50/50 flex items-center justify-between">
                            <p className="text-xs text-sky-700" style={{ fontWeight: 600 }}>
                              {cat.emoji} {cat.label}
                              {pageTemplates.length > 0 && (
                                <span className="text-[10px] text-sky-500 mr-2" style={{ fontWeight: 400 }}>
                                  {" \u2014 "}{pageTemplates.length}{" \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD"}
                                </span>
                              )}
                            </p>
                            <div className="flex items-center gap-1">
                              <button
                                type="button"
                                onClick={loadPageTemplates}
                                className="w-5 h-5 rounded flex items-center justify-center text-sky-400 hover:text-sky-600 hover:bg-sky-100 transition-colors"
                                title={"\u05E8\u05E2\u05E0\u05DF"}
                              >
                                <RefreshCw className={`w-3 h-3 ${pagesLoading ? "animate-spin" : ""}`} />
                              </button>
                            </div>
                          </div>

                          {/* Save current page button */}
                          <div className="px-3 pt-2 pb-1">
                            <button
                              type="button"
                              onClick={() => setShowSavePageDialog(true)}
                              className="w-full flex items-center justify-center gap-1.5 px-3 py-2 text-[12px] bg-sky-500 text-white rounded-lg hover:bg-sky-600 transition-colors"
                              style={{ fontWeight: 600 }}
                            >
                              <Save className="w-3.5 h-3.5" />
                              {"\u05E9\u05DE\u05D5\u05E8 \u05E2\u05DE\u05D5\u05D3 \u05E0\u05D5\u05DB\u05D7\u05D9 \u05DB\u05EA\u05D1\u05E0\u05D9\u05EA"}
                            </button>
                          </div>

                          <div className="p-2">
                            {pagesLoading && pageTemplates.length === 0 ? (
                              <p className="text-[11px] text-gray-400 text-center py-3">{"\u05D8\u05D5\u05E2\u05DF..."}</p>
                            ) : pageTemplates.length === 0 ? (
                              <p className="text-[11px] text-gray-400 text-center py-3">
                                {"\u05D0\u05D9\u05DF \u05E2\u05D3\u05D9\u05D9\u05DF \u05EA\u05D1\u05E0\u05D9\u05D5\u05EA \u05E2\u05DE\u05D5\u05D3\u05D9\u05DD \u05E9\u05DE\u05D5\u05E8\u05D5\u05EA"}
                              </p>
                            ) : (
                              pageTemplates.map((t) => (
                                <PageTemplateItem
                                  key={t.id}
                                  template={t}
                                  onDeleted={loadPageTemplates}
                                  onLoadToCurrentPage={handleLoadPageTemplate}
                                />
                              ))
                            )}
                          </div>
                        </div>
                      );
                    }

                    /* ── Regular block categories ── */
                    const blocks = filteredBlocks.filter((b) => b.category === cat.key);
                    if (blocks.length === 0) return null;

                    return (
                      <div key={cat.key} className="border-b border-gray-100 last:border-0">
                        <div className={`px-4 py-2 ${
                          cat.key === "sections" && isStore ? "bg-purple-50/50" :
                          cat.key === "sections" ? "bg-amber-50/50" : "bg-gray-50/50"
                        }`}>
                          <p className={`text-xs ${
                            cat.key === "sections" && isStore ? "text-purple-700" :
                            cat.key === "sections" ? "text-amber-700" : "text-gray-500"
                          }`} style={{ fontWeight: 600 }}>
                            {cat.emoji} {cat.label}
                            {cat.key === "sections" && isStore && (
                              <span className="text-[10px] text-purple-500 mr-2" style={{ fontWeight: 400 }}>{"\u2014 \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05DC\u05D7\u05E0\u05D5\u05EA \u05D5\u05DB\u05DC\u05DC\u05D9\u05D9\u05DD"}</span>
                            )}
                            {cat.key === "sections" && !isStore && (
                              <span className="text-[10px] text-amber-500 mr-2" style={{ fontWeight: 400 }}>{"\u2014 \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05E9\u05DC\u05DE\u05D9\u05DD"}</span>
                            )}
                          </p>
                        </div>
                        <div className="p-2">
                          {blocks.map((block) => (
                            <PaletteItem key={block.type} block={block} />
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Footer */}
              <div className="px-4 py-3 border-t border-gray-100 bg-gray-50/30">
                <p className="text-[10px] text-gray-400 text-center">
                  {"\u05E0\u05D9\u05EA\u05DF \u05D2\u05DD \u05DC\u05DC\u05D7\u05D5\u05E5 \u05E2\u05DC + \u05D1\u05D9\u05DF \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05DC\u05D4\u05D5\u05E1\u05E4\u05D4 \u05D9\u05D3\u05E0\u05D9\u05EA \u2022 \uD83D\uDCBE \u05E9\u05DE\u05E8\u05D5 \u05E1\u05E7\u05E9\u05E0\u05D9\u05DD \u05DE\u05D5\u05DB\u05E0\u05D9\u05DD \u05DC\u05E1\u05E4\u05E8\u05D9\u05D9\u05D4"}
                </p>
              </div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Save Page Dialog */}
        <SavePageDialog
          isOpen={showSavePageDialog}
          onClose={() => setShowSavePageDialog(false)}
          onSaved={loadPageTemplates}
        />
      </PageTemplatesContext.Provider>
    </SavedSectionsContext.Provider>
  );
}
